
import { Vue, Options } from 'vue-class-component';
import Summary from "./summary.vue";
import { HangingBkdSummaryConsole, HangingConsoleBkdListRequest, ConfirmHangingBkdConsole, DownloadHangingBkdConsole } from '@/domain/entities/HangingBkd';
import { CardHangingShared } from '@/domain/entities/CardHangingShared';
import { AccountController } from '@/app/ui/controllers/AccountController';
import { SttHangingController } from '@/app/ui/controllers/SttHangingController';
import { debounce } from 'lodash';
import { MainAppController } from '@/app/ui/controllers/MainAppController';
import ConfirmAction from "@/app/ui/views/dashboard/component/console/hanging/components/ConfirmAction.vue";
import CopyTemplate from "@/app/ui/views/dashboard/component/console/hanging/components/CopyTemplate.vue";
import Filter from "../tab-hanging-incoming/components/filter.vue";
import CardProgress from "../tab-hanging-incoming/components/card-progress.vue";
import Actions from "../tab-hanging-incoming/components/actions.vue";
import CardCollapsible from "../shared/card-collapsible-hanging.vue";
import { ConfirmHangingOutgoingRequest } from '@/domain/entities/HangingOutgoing';
import { SubmitHangingConfirmationUpdateStatus } from '@/data/payload/api/HangingApiRequest';

@Options({
  components: {
    Summary,
    Filter,
    CardCollapsible,
    ConfirmAction,
    CopyTemplate,
    CardProgress,
    Actions
  }
})
class TabHangingBkdConsole extends Vue {
  dataSummary = new HangingBkdSummaryConsole();
  isLoadingFetch = false;
  isLoadingList = false;
  hangingBkdConsoleList: CardHangingShared[] = [];
  filter = new HangingConsoleBkdListRequest();
  openConfirmUpdateStatus = false;
  messageHtml = "";
  lastStatus = "";
  isOpenTemplate = false;
  payloadUpdateStatus = new ConfirmHangingOutgoingRequest();
  selectedCard = {
    stt: [] as string [],
    groupName: ""
  }

  mounted() {
    this.getHangingOutgoingSummary();
    this.getHangingOutgoingList();
  }

  get isEmptyList() {
    const isEmptyFilter = Object.values(this.filter).every(value => value === "" || value == null);
    console.log("isEmptyFilter: ", isEmptyFilter)
    return !this.hangingBkdConsoleList.length && !this.isLoadingList && isEmptyFilter;
  }

  get accountData() {
    return AccountController.accountData;
  }

  async getHangingOutgoingSummary() {
    try {
      this.isLoadingFetch = true;
      const res = await SttHangingController.getHangingConsoleBkdSummary();
      this.dataSummary = res;
    } catch (error) {
      console.log("error: ", error);
    } finally {
      this.isLoadingFetch = false;
    }
  }

  async getHangingOutgoingList(params: HangingConsoleBkdListRequest = new HangingConsoleBkdListRequest()) {
    try {
      this.isLoadingList = true;
      const resp = await SttHangingController.getHangingConsoleBkdList(params);
      this.hangingBkdConsoleList = resp;
    } catch (error) {
      this.hangingBkdConsoleList = [];
    } finally {
      this.isLoadingList = false;
    }
  }

  getFilter = debounce((filter: any) => {
    const params = new HangingConsoleBkdListRequest({ existenceStatus: filter.existenceStatus, days: filter.days, search: filter.search, statusFollowUp: filter.statusFollowUp });
    this.filter = params;
    this.getHangingOutgoingList(this.filter);
  }, 250)

  downloadHangingBkdConsole(stts: string[] = []) {
    SttHangingController.downloadHangingBkdConsole(new DownloadHangingBkdConsole({
      listStt: stts,
      hubId: 0 // will set in the constructor
    }))
  }

  updateStatus(status: string) {
    this.lastStatus = status;
    this.messageHtml = this.$t("dashboard.hanging.confirmUpdateMessage", { status })
    this.openConfirmUpdateStatus = true
  }

  onSuccessUpdateStatus() {
    this.getHangingOutgoingSummary();
    this.getHangingOutgoingList(this.filter);
  }

  async onClickTemplateMessage(item: CardHangingShared) {
    this.selectedCard = {
      stt: item.groups.flatMap((group) => group.stts).map((stt) => stt.sttNo),
      groupName: item.groups.flatMap((group) => group.groupName).join(",")
    }
    await this.$nextTick()
    this.isOpenTemplate = true;
  }

   async onConfirmDone(data: CardHangingShared){
    try {
      MainAppController.closeErrorMessage();
      MainAppController.showLoading();

      await SttHangingController.confirmHangingBkdConsole(
        new ConfirmHangingBkdConsole({
          activityType: data.activityType,
          followUpPartnerName: data.followUp.partnerName,
          followUpPartnerCode: data.followUp.partnerCode,
          followUpPartnerType: data.followUp.partnerType,
          username: data.followUp.username
        })
      );

      MainAppController.setSnackbarMessage(
        `<div class="font-bold">${this.$t("dashboard.hanging.successUpdate")}</div>`
      );
      MainAppController.setSnackbarPositivity(true);
      MainAppController.setSnackbarTimeout(2000);
      MainAppController.setSnackbar(true);

      this.onSuccessUpdateStatus();
    } catch {
      MainAppController.setSnackbarMessage(
        `<div class="font-bold">${this.$t("dashboard.hanging.failUpdate")}</div>`
      );
      MainAppController.setSnackbarPositivity(false);
      MainAppController.setSnackbarTimeout(2000);
      MainAppController.setSnackbar(true);
    } finally {
      MainAppController.closeLoading();
    }
  };

  async onUpdateStatus() {
    try {
      this.openConfirmUpdateStatus = false;
      MainAppController.showLoading();
      const statusUppercase = this.lastStatus.toUpperCase();
      const listStt = this.hangingBkdConsoleList
        .flatMap(item => item.groups)
        .flatMap(group => group.stts)
        .filter(stt => stt.isChecked)
        .map(stt => stt.sttNo);

      const payload = new SubmitHangingConfirmationUpdateStatus({
        customProcessStatus: statusUppercase,
        sttNo: listStt,
        remarks: "Hanging",
        pageType: "hanging"
      })
      const resp = await SttHangingController.submitHangingConfirmationUpdateStatus(payload)
      const message = resp.totalSttFailed
        ? `<div class="text-base">${this.$t("dashboard.hanging.successUpdateStatusWithRatio", { status: this.lastStatus, success: resp.totalSttSuccess, total: resp.totalSttSuccess + resp.totalSttFailed })}</div>`
        : `<div class="text-base">${this.$t("dashboard.hanging.successUpdateStatus", { status: this.lastStatus })}</div>`;
      const color = resp.totalSttFailed ? '#FAB713' : '#36B37E';
      MainAppController.setSnackbarMessage(message);
      MainAppController.setSnackbarPositivity(true);
      MainAppController.setSnackbarTimeout(2000);
      MainAppController.setSnackbarBackgroundColor(color);
      MainAppController.setSnackbar(true);
      this.onSuccessUpdateStatus()
    } catch (err) {
      MainAppController.setSnackbarMessage(`<div class="text-base">${this.$t("dashboard.hanging.failUpdateStatus", { status: this.lastStatus })}</div>`);
      MainAppController.setSnackbarPositivity(false);
      MainAppController.setSnackbarTimeout(2000);
      MainAppController.setSnackbar(true);
    } finally {
      MainAppController.closeLoading();
    }
  }

  get accountTypeMsg() {
    return this.accountData.isPosAccount
      ? this.$t("dashboard.hanging.accountTypePos")
      : this.$t("dashboard.hanging.accountTypeKonsolidator");
  }
}

export default TabHangingBkdConsole;
